import { createContext, useContext, useEffect, useState } from 'react';
import { usePhysicalUnitsAPI } from 'service/PhysicalUnitsAPIService';
import { UserFeedbackContext } from './UserFeedbackContext';

export const PhysicalUnitContext = createContext();

export const PhysicalUnitContextProvider = ({ id, ...props }) => {

    const _id = id;
    const [physicalUnit, setPhysicalUnit] = useState(null);
    const [loadingPhysicalUnit, setLoadingPhysicalUnit] = useState(true);

    const { setAndShowErrorMessage, setAndShowSuccessMessage } = useContext(UserFeedbackContext);
    
    const [submitting, setSubmitting] = useState(false);

    const service = usePhysicalUnitsAPI();

    const get = () => {
        return service.fetch(_id);
    }
    
    const tariffPlans = () => {
        return service.tariffPlans(_id);
    }

    const energy = (period, date) => {
        return service.energy(_id, period, date);
    }

    const activeContracts = (filters = []) => {
        return service.activeContracts(_id, filters);
    }

    const clients = (filters = []) => {
        return service.clients(_id, filters);
    }

    const update = async (data) => {
        setSubmitting(true);

        try {
            const response = await service.update(_id, data);
            setPhysicalUnit(response.data);
            return response;
        } catch (e) {
            throw e;
        } finally {
            setSubmitting(false);
        }
    }

    const updateImage = async (data, action) => {
        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("image", data);
            formData.append("action", action);

            const response = await service.updateImage(_id, formData);
            setPhysicalUnit((prev) => ({
                ...prev,
                image: response.data.image,
            }));
            return response;
        } catch (e) {
            throw e;
        } finally {
            setSubmitting(false);
        }
    }

    const changeOwner = (data, onSuccess) => {
        setSubmitting(true);
        service.changeOwner(_id, data).then((response) => {
            setPhysicalUnit(response.data);
            setSubmitting(false);
            setAndShowSuccessMessage("success.api.save");

            if (onSuccess) {
                onSuccess(response);
            }

        }).catch((e) => {
            setAndShowErrorMessage(e);
            setSubmitting(false);
        })
    }

    const createInstallation = (data, onSuccess) => {
        setSubmitting(true);
        service.createInstallation(data).then((response) => {
            setPhysicalUnit(response.data);
            setSubmitting(false);
            setAndShowSuccessMessage("success.api.save");

            if (onSuccess) {
                onSuccess(response);
            }

        }).catch((e) => {
            setAndShowErrorMessage(e);
            setSubmitting(false);
        })
    }

    const remove = async () => {
        setSubmitting(true);
        try {
            const response = await service.remove(_id);
            return response;
        } catch (e) {
            throw e;
        } finally {
            setSubmitting(false);
        }
    }

    const returnObject = {
        // Methods
        get,
        tariffPlans,
        energy,
        activeContracts,
        clients,
        update,
        changeOwner,
        updateImage,
        createInstallation,
        remove,
        // Attributes
        physicalUnit,
        loadingPhysicalUnit,
        submitting
    };

    

    useEffect(() => {
        if (id) {
            get().then((response) => {
                setPhysicalUnit(response.data);
                setLoadingPhysicalUnit(false);
            }).catch((error) => {
                setLoadingPhysicalUnit(false);
                console.error(error);
            });
        }
    }, [id]);

    return (
        <PhysicalUnitContext.Provider value={returnObject}>
            {props.children}
        </PhysicalUnitContext.Provider>
    );
};

export const usePhysicalUnitContext = () => {
    const context = useContext(PhysicalUnitContext);
    if (!context) {
        throw new Error('usePhysicalUnitContext must be used within a PhysicalUnitContextProvider');
    }
    return context;
}